import { nextTick, reactive, ref } from 'vue';
import { Bus, useNotif, useValidation } from '@orion.ui/orion';
import BaseSetupService from '@/setup/BaseSetupService';
import AuthApi from '@/api/AuthApi';
import { debounce } from 'lodash-es';
import useOrganizationUserService from '@/services/organization-user/OrganizationUserService';
import useUserService from '@/services/user/UserService';

type Props = SetupProps<typeof OrganizationUserAsideNewSetupService.props>

export default class OrganizationUserAsideNewSetupService extends BaseSetupService<Props> {
	static readonly props = {};

	private _aside?: OrionAside;
	private readonly state = reactive({
		emailChecked: false,
		userExist: false,
	});

	readonly _firstName = ref<OrionInput>();

	user = reactive({
		userId: 0,
		userType: this.self.userType,
		organizationId: this.self.organization?.id,
		email: undefined as Undef<string>,
		firstName: undefined as Undef<string>,
		lastName: undefined as Undef<string>,
	} as IAddUserFromFrontModel);

	validator = useValidation(this.user, {
		email: 'required|email',
		firstName: 'required',
		lastName: 'required',
	});

	debouncedCheckEmailAsync = debounce(() => this.checkEmailAsync(), 200);


	get emailChecked () { return this.state.emailChecked; }
	get userExist () { return this.state.userExist; }


	constructor (props: Props, _aside?: OrionAside) {
		super(props);
		this._aside = _aside;
	}


	goToStep1 () {
		this.state.emailChecked = false;
		this.state.userExist = false;
		this.user.firstName = undefined;
		this.user.lastName = undefined;
		this.user.userId = 0;
	}

	async checkEmailAsync () {
		if (this.validator.checkRuleParams(this.user.email, 'required|email')) {
			try {
				this._aside?._loader()?.show(`Vérification de l'email...`);
				const { data } = await AuthApi.checkLogin(this.user.email);

				if (data) {
					this.state.userExist = true;
					this.user.firstName = data.userFirstName;
					this.user.lastName = data.userLastName;
					this.user.userId = data.userId;
				}

				this.state.emailChecked = true;

				nextTick(() => this._firstName.value?.focus());
			} catch (error: any) {
				useNotif.danger(error?.response?.data);
			} finally {
				this._aside?._loader()?.hide();
			}
		} else {
			useNotif.danger('Vous devez saisir un email valide');
		}
	}

	async associateUserAsync () {
		await useOrganizationUserService().storeAsync({
			userId: this.user.userId,
			organizationId: this.user.organizationId,
		});
		this._aside?.close();
	}

	async createUserAsync () {
		if (this.validator.validate()) {
			this._aside?._loader()?.show(`Création de l'utilisateur...`);

			try {
				const newUser = await useUserService().addAsync(this.user);
				if (!!newUser) {
					Bus.emit('OrganizationUserPagedListViewSetupService:refresh-list', true);
					useNotif.success(`Utilisateur créé avec succès`);
					await this._aside?.close();
					this.router.push({
						name: 'OrganizationUserDetails',
						params: { id: newUser.id },
					});
				}
			} catch (error: any) {
				useNotif.danger(error?.response?.data);
			} finally {
				this._aside?._loader()?.hide();
			}
		} else {
			this.validator.showValidationState();
		}
	}
}
